import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ENTRY_LANGUAGES,
  ENTRY_ONLY_LANGUAGES,
  isEntryOnlyLanguage,
} from '@/Config/app/entryLocale';
import { changeAppLanguage } from '@/Config/app/i18n';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { UI_LANGUAGES } from '@/Domain/enum/SupportLanguage.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

export const LanguageSwitcher = ({ entry = false }: { entry?: boolean }) => {
  const { t, i18n } = useTranslation('entry_language');
  const { capture } = usePostHogAnalytics();
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  const currentLanguage = i18n.resolvedLanguage || 'en';
  const languages = entry
    ? ENTRY_LANGUAGES
    : [...UI_LANGUAGES, ...ENTRY_ONLY_LANGUAGES.filter(({ code }) => code === currentLanguage)];

  const handleChangeLanguage = async (lng: string): Promise<void> => {
    if (isChangingLanguage) return;
    const previousLanguage = i18n.resolvedLanguage;
    setIsChangingLanguage(true);
    setHasLoadError(false);
    try {
      await changeAppLanguage(lng);
      capture(PostHogEventEnum.UiLanguageChanged, {
        language: lng,
        previous_language: previousLanguage,
      });
    } catch {
      setHasLoadError(true);
    } finally {
      setIsChangingLanguage(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Select
        value={currentLanguage}
        onValueChange={handleChangeLanguage}
        disabled={isChangingLanguage}
        dir={i18n.dir()}
      >
        <SelectTrigger
          className="min-w-[120px] max-w-full h-[39px]"
          data-testid="language-switch"
          aria-busy={isChangingLanguage}
          aria-label={t('label')}
        >
          <span className="inline-flex items-center gap-2">
            <SelectValue placeholder={t('label')} />
            {isChangingLanguage && <Spinner className="h-4 w-4" aria-hidden />}
          </span>
        </SelectTrigger>
        <SelectContent collisionPadding={8} className="max-w-[calc(100vw-1rem)]">
          {languages.map(({ code, label }) => (
            <SelectItem key={code} value={code} lang={code} data-testid={`language-option-${code}`}>
              {label}
              {isEntryOnlyLanguage(code) && ` (${t('partial_label')})`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasLoadError && (
        <p
          className="text-xs text-destructive max-w-xs"
          role="alert"
          data-testid="language-load-error"
        >
          {t('common:language_load_failed')}
        </p>
      )}
    </div>
  );
};
